/**
 * InlineGroup — 将相邻 inline 块分组渲染为对应的 Markdown 容器。
 *
 * 当 inline-render 和 inline-ref 块与 markdown 文本相邻时，groupBlocks()
 * 将它们分组在一起形成连续段落、标题、列表项或引用块。此组件渲染该分组。
 */

import type { DocumentBlock, SourceSpan } from "@magic-md/core";
import { createElement, Fragment, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import rehypeKatex from "rehype-katex";
import remarkDefinitionList, { defListHastHandlers } from "remark-definition-list";
import remarkFrontmatter from "remark-frontmatter";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import {
    assetRefUrlTransform,
    createAssetRefRemarkPlugin,
    withAssetRefMarkdownComponents,
} from "../asset-refs";
import {
    createAnnotationSourceRehypePlugin,
} from "../../annotations/markdown-rehype";
import { useAnnotationContextOptional } from "../../annotations/AnnotationProvider";
import { useDocContext } from "../contexts/DocContext";
import { createTocHeadingComponents, normalizeRenderedMarkdown } from "../utils";
import { InlineRef } from "./InlineRef";
import { InlineWrapper } from "./InlineWrapper";
import { PluginBlock } from "./PluginBlock";

export function InlineGroup({
    blocks,
    origBlocks,
    blockIndexes,
}: {
    blocks: DocumentBlock[];
    origBlocks: DocumentBlock[];
    blockIndexes?: ReadonlyMap<DocumentBlock, number>;
}) {
    const { documentPath, importDocuments, openFile, openExternal } = useDocContext();
    const annotationContext = useAnnotationContextOptional();

    const renderInlineBlock = (
        block: DocumentBlock,
        i: number,
        markdownContent?: string,
        context: "paragraph" | "heading" = "paragraph",
    ): ReactNode => {
        if (block.type === "inline-render") {
            return (
                <InlineWrapper
                    key={`inline-${block.definitionId}-${i}`}
                    definitionId={block.definitionId}
                >
                    <PluginBlock
                        definitionId={block.definitionId}
                        attrs={block.attrs}
                        mode="inline"
                    />
                </InlineWrapper>
            );
        }

        if (block.type === "markdown") {
            const blockIndex = blockIndexes?.get(block) ?? origBlocks.indexOf(block);
            const content = markdownContent ?? block.content;
            const [leadingWhitespace, markdownCore, trailingWhitespace] =
                context === "heading"
                    ? splitBoundaryWhitespace(content)
                    : ["", content, ""];
            const annotationSource = getAdjustedMarkdownSource(block, markdownCore);
            const annotationRehypePlugin =
                annotationSource && annotationContext
                    ? createAnnotationSourceRehypePlugin({
                        documentPath: annotationContext.documentPath,
                        content: markdownCore,
                        source: annotationSource,
                        annotations: annotationContext.annotations,
                    })
                    : null;
            const rehypePlugins = annotationRehypePlugin
                ? [annotationRehypePlugin, rehypeKatex, rehypeHighlight]
                : [rehypeKatex, rehypeHighlight];
            return (
                <Fragment key={`md-${i}`}>
                    {leadingWhitespace || null}
                    {markdownCore ? (
                        <ReactMarkdown
                            key={`md-${i}`}
                            remarkPlugins={[
                                remarkGfm,
                                [remarkFrontmatter, ["yaml", "toml"]],
                                remarkDefinitionList,
                                remarkMath,
                                createAssetRefRemarkPlugin(documentPath, importDocuments),
                            ]}
                            urlTransform={assetRefUrlTransform}
                            remarkRehypeOptions={{ handlers: defListHastHandlers }}
                            rehypePlugins={rehypePlugins}
                            components={{
                                ...withAssetRefMarkdownComponents(
                                    documentPath,
                                    createTocHeadingComponents(
                                        blockIndex >= 0 ? blockIndex : i,
                                    ),
                                    { openFile, openExternal },
                                    importDocuments,
                                ),
                                p: ({ children }) => <>{children}</>,
                            }}
                        >
                            {normalizeRenderedMarkdown(markdownCore)}
                        </ReactMarkdown>
                    ) : null}
                    {trailingWhitespace || null}
                </Fragment>
            );
        }

        if (block.type === "inline-ref") {
            return (
                <InlineRef
                    key={`ref-${block.refPath}-${i}`}
                    definitionId={block.definitionId}
                    accessor={block.accessor}
                    outputPath={block.outputPath}
                    refPath={block.refPath}
                    variant={context === "heading" ? "heading" : "inline"}
                />
            );
        }

        return null;
    };

    const heading = getInlineHeading(blocks, origBlocks, blockIndexes);
    if (heading) {
        const children = blocks.map((block, i) =>
            renderInlineBlock(
                block,
                i,
                i === heading.blockPosition ? heading.content : undefined,
                "heading",
            ),
        );

        return createElement(
            `h${heading.level}`,
            {
                "data-toc-id": `toc-h-${heading.blockIndex}-0`,
            },
            children,
        );
    }

    const listItem = getInlineListItem(blocks, origBlocks, blockIndexes);
    if (listItem) {
        const children = blocks.map((block, i) =>
            renderInlineBlock(
                block,
                i,
                i === listItem.blockPosition ? listItem.content : undefined,
            ),
        );
        return createElement(
            listItem.ordered ? "ol" : "ul",
            listItem.ordered ? { start: listItem.start } : null,
            createElement("li", null, children),
        );
    }

    const blockquote = getInlineBlockquote(blocks, origBlocks, blockIndexes);
    if (blockquote) {
        const children = blocks.map((block, i) =>
            renderInlineBlock(
                block,
                i,
                i === blockquote.blockPosition ? blockquote.content : undefined,
            ),
        );
        return createElement("blockquote", null, createElement("p", null, children));
    }

    return (
        <p>
            {blocks.map((block, i) => renderInlineBlock(block, i))}
        </p>
    );
}

function getAdjustedMarkdownSource(
    block: Extract<DocumentBlock, { type: "markdown" }>,
    content: string,
): SourceSpan | undefined {
    if (!block.source) return undefined;
    if (content === block.content) return block.source;
    const localStart = block.content.indexOf(content);
    if (localStart < 0) return undefined;
    return {
        ...block.source,
        startOffset: block.source.startOffset + localStart,
        endOffset: block.source.startOffset + localStart + content.length,
    };
}

function splitBoundaryWhitespace(content: string): [string, string, string] {
    const leading = content.match(/^\s+/)?.[0] ?? "";
    const trailing = content.match(/\s+$/)?.[0] ?? "";
    const start = leading.length;
    const end = trailing.length > 0 ? content.length - trailing.length : content.length;
    return [leading, content.slice(start, end), trailing];
}

const LEADING_BLANK_LINES_RE = "(?:[ \\t]*\\r?\\n)*";
const INLINE_HEADING_RE = new RegExp(`^${LEADING_BLANK_LINES_RE} {0,3}(#{1,6})[ \\t]+([^\\n]*)$`);
const INLINE_UNORDERED_LIST_ITEM_RE = new RegExp(`^${LEADING_BLANK_LINES_RE} {0,3}([-+*])[ \\t]+([^\\n]*)$`);
const INLINE_ORDERED_LIST_ITEM_RE = new RegExp(`^${LEADING_BLANK_LINES_RE} {0,3}(\\d+)[.)][ \\t]+([^\\n]*)$`);
const INLINE_BLOCKQUOTE_RE = new RegExp(`^${LEADING_BLANK_LINES_RE} {0,3}>[ \\t]?([^\\n]*)$`);

function getInlineHeading(
    blocks: DocumentBlock[],
    origBlocks: DocumentBlock[],
    blockIndexes?: ReadonlyMap<DocumentBlock, number>,
): {
    level: number;
    content: string;
    blockIndex: number;
    blockPosition: number;
} | null {
    const firstMarkdown = blocks[0];
    if (!firstMarkdown || firstMarkdown.type !== "markdown") return null;

    const match = firstMarkdown.content.match(INLINE_HEADING_RE);
    if (!match) return null;

    const blockIndex = blockIndexes?.get(firstMarkdown) ?? origBlocks.indexOf(firstMarkdown);
    return {
        level: match[1].length,
        content: match[2],
        blockIndex: blockIndex >= 0 ? blockIndex : 0,
        blockPosition: 0,
    };
}

function getInlineListItem(
    blocks: DocumentBlock[],
    origBlocks: DocumentBlock[],
    blockIndexes?: ReadonlyMap<DocumentBlock, number>,
): {
    ordered: boolean;
    start?: number;
    content: string;
    blockIndex: number;
    blockPosition: number;
} | null {
    const firstMarkdown = blocks[0];
    if (!firstMarkdown || firstMarkdown.type !== "markdown") return null;

    const orderedMatch = firstMarkdown.content.match(INLINE_ORDERED_LIST_ITEM_RE);
    const unorderedMatch = firstMarkdown.content.match(INLINE_UNORDERED_LIST_ITEM_RE);
    const match = orderedMatch ?? unorderedMatch;
    if (!match) return null;

    const blockIndex = blockIndexes?.get(firstMarkdown) ?? origBlocks.indexOf(firstMarkdown);
    return {
        ordered: Boolean(orderedMatch),
        ...(orderedMatch ? { start: Number(orderedMatch[1]) } : {}),
        content: match[2],
        blockIndex: blockIndex >= 0 ? blockIndex : 0,
        blockPosition: 0,
    };
}

function getInlineBlockquote(
    blocks: DocumentBlock[],
    origBlocks: DocumentBlock[],
    blockIndexes?: ReadonlyMap<DocumentBlock, number>,
): {
    content: string;
    blockIndex: number;
    blockPosition: number;
} | null {
    const firstMarkdown = blocks[0];
    if (!firstMarkdown || firstMarkdown.type !== "markdown") return null;

    const match = firstMarkdown.content.match(INLINE_BLOCKQUOTE_RE);
    if (!match) return null;

    const blockIndex = blockIndexes?.get(firstMarkdown) ?? origBlocks.indexOf(firstMarkdown);
    return {
        content: match[1],
        blockIndex: blockIndex >= 0 ? blockIndex : 0,
        blockPosition: 0,
    };
}
